/**
 * Fetches the project-scoped managed-API connector resource
 * (`/automationProjects/{project}/managedApis/{name}`). Mirrors LogicAppsUX's
 * `useConnector`; resolves the project-scoped URL because short-form IDs
 * (`/managedApis/{name}`) lack `connectionParameterSets`.
 */

import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useAuth } from '@/auth/useAuth';
import { useAppStore } from '@/stores/appStore';
import { apiVersions, getLogicEndpoint } from '@/services/endpoints';
import { extractConnectorName, isManagedApiConnector } from '@/utils/connections';

const STALE_MS = 8 * 60 * 60 * 1000;

export function useConnector(connectorId: string | undefined | null): UseQueryResult<unknown, Error> {
  const { getOttoToken } = useAuth();
  const { selectedApp } = useAppStore();
  const projectName = selectedApp?.projectName;

  const connectorName = useMemo(
    () => (connectorId ? extractConnectorName(connectorId).toLowerCase() : ''),
    [connectorId]
  );

  const isEnabled =
    !!projectName && !!connectorName && !!connectorId && isManagedApiConnector(connectorId);

  return useQuery({
    queryKey: ['otto.connector', projectName ?? '', connectorName],
    queryFn: async () => {
      const token = await getOttoToken();
      if (!token) return null;
      const url = `${getLogicEndpoint()}/automationProjects/${encodeURIComponent(projectName ?? '')}/managedApis/${encodeURIComponent(connectorName)}?api-version=${apiVersions.otto}`;
      const response = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      // 404 caches null; other errors throw so React Query retries instead of caching a bad null for 8h.
      if (response.status === 404) return null;
      if (!response.ok) {
        throw new Error(`Failed to fetch connector (${response.status})`);
      }
      return response.json();
    },
    enabled: isEnabled,
    staleTime: STALE_MS,
    gcTime: STALE_MS,
    retry: 2,
  });
}
